import { t } from '@lingui/macro';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';

import { update as updateFilters } from '@api/v3/search_filters';
import renderModal from '@components/show_page/modals/renderModal';
import { unsavedChangesOptions } from '@helpers/sweet_alert_options';

import ButtonsContainer from './buttons_container';
import ModalBodyContent from './modal_body_content';
import ModalHeader from './modal_header';
import SearchPinsAdd from './search_pins_add';

import './styles/manage_search_pins.scss';

const focusNotificationMessage = (notification, message) => {
  const titleElement = notification?.$el?.[0]?.querySelector('.m-notification-title');
  if (!titleElement) {
    return;
  }

  titleElement.setAttribute('tabindex', '-1');
  titleElement.setAttribute('role', 'status');
  titleElement.setAttribute('aria-label', message);
  titleElement.focus({ preventScroll: true });
};

const ManageSearchPinsModal = (props) => {
  const [editingActive, setEditingActive] = useState(false);
  const [saveButtonDisabled, setSaveButtonDisabled] = useState(true);
  const [dragInProgress, setDragInProgress] = useState(false);
  const [srAnnouncement, setSrAnnouncement] = useState('');
  const [shouldRefocusModal, setShouldRefocusModal] = useState(false);
  const announcerRef = useRef(null);

  const { closeModal, searchFilters, updateSearchFilters } = props;

  // Function to announce changes to screen reader users within the modal
  const announceToScreenReader = useCallback((message) => {
    setSrAnnouncement('');
    window.requestAnimationFrame(() => {
      setSrAnnouncement(message);
    });
  }, []);

  const reorderRows = (rowsData) => (
    rowsData.map((pin, i) => {
      const updatedPin = pin;
      updatedPin.position = i;
      return updatedPin;
    })
  );

  const removeRow = (stateArgument, indexToRemove) => {
    const newState = [];
    stateArgument.forEach((row, i) => {
      if (i !== indexToRemove) {
        newState.push({ ...row });
      }
    });
    return newState;
  };

  const formatSearchFiltersData = (searchFiltersRaw) => (
    searchFiltersRaw.map(({ featured, key, label, position, query }) => ({ featured, key, label, position, query }))
  );

  const reducer = (state, action) => {
    const { data, index, type } = action;

    switch (type) {
      case 'delete-row': {
        const newState = removeRow(state, index);
        // Trigger focus management after deletion
        setShouldRefocusModal(true);
        // Delay announcement to ensure DOM updates first
        announceToScreenReader('Search pin deleted');
        return newState;
      }
      case 'update-featured':
        return state.map((row, i) => {
          if (i === index) {
            const prevFeatured = { ...row }.featured;
            const newRow = { ...row };
            newRow.featured = !prevFeatured;
            return newRow;
          }
          return row;
        });
      case 'done-editing':
        return state.map((row, i) => {
          if (i === index) {
            return data;
          }
          return row;
        });
      case 'initial-load': {
        return formatSearchFiltersData(searchFilters); // set reducer state
      }
      case 'update-row-position': {
        const draggedRow = state[index];
        const { hoveredRow } = data;

        if (draggedRow.key === hoveredRow.key) { return [...state]; } // can't drop on itself

        const newState = removeRow(state, index); // delete pin in previous position
        const hoveredRowIndex = newState.map((pin) => pin.key).indexOf(hoveredRow.key);
        const insertionIndex = hoveredRow.hoveredHalf === 'top'
          ? hoveredRowIndex
          : hoveredRowIndex + 1;

        newState.splice(insertionIndex, 0, draggedRow); // insert pin into updated position

        return reorderRows(newState);
      }
      case 'alphabetize': {
        const updatedState = [...state].sort((a, b) => {
          if (a.label.toUpperCase() < b.label.toUpperCase()) { return -1; }
          if (a.label.toUpperCase() > b.label.toUpperCase()) { return 1; }
          return 0;
        });
        announceToScreenReader('Search pins sorted alphabetically');
        return reorderRows(updatedState);
      }
      case 'add-new-pin': {
        const updatedState = [...state];
        updatedState.push(data);
        announceToScreenReader('New search pin added');
        return updatedState;
      }
      default: return state;
    }
  };

  const [updatedFiltersData, dispatch] = useReducer(reducer, []);

  const enableDisableSave = () => {
    const initialFiltersData = formatSearchFiltersData(searchFilters);
    let isDataUpdated = false;

    if (initialFiltersData.length !== updatedFiltersData.length) {
      isDataUpdated = true;
    } else {
      initialFiltersData.forEach((initialFilter) => {
        const filterToCompare = updatedFiltersData.filter((updatedFilter) => (initialFilter.key === updatedFilter.key));

        if (!filterToCompare.length) {
          isDataUpdated = true;
        } else {
          Object.keys(filterToCompare[0]).forEach((filterKey) => {
            if (initialFilter[filterKey] !== filterToCompare[0][filterKey]) {
              isDataUpdated = true;
            }
          });
        }
      });
    }

    setSaveButtonDisabled(!isDataUpdated);
  };

  useEffect(() => {
    dispatch({ type: 'initial-load' });
  }, [searchFilters]);

  useEffect(() => {
    enableDisableSave();
  }, [updatedFiltersData]);

  // Handle focus management after deletion
  useEffect(() => {
    if (shouldRefocusModal && announcerRef.current) {
      announcerRef.current.focus();
      setShouldRefocusModal(false);
    }
  }, [shouldRefocusModal]);

  const handleClose = () => {
    if (!saveButtonDisabled) {
      window.swal(unsavedChangesOptions({}), (confirmSelected) => {
        if (confirmSelected) { closeModal(); }
      });
    } else { closeModal(); }
  };

  const handleSubmit = () => {
    const message = t`Search pins updated!`;

    updateFilters(updatedFiltersData); // update endpoint
    updateSearchFilters(updatedFiltersData); // update show page state

    const notification = Notify.create({
      title: message,
      type: 'success',
    });

    closeModal();

    window.setTimeout(() => {
      focusNotificationMessage(notification, message);
    }, 600);
  };

  return (
    <div className="manage-search-pins-modal-wrapper">
      {/* ARIA live region for announcing changes to screen reader users */}
      <div
        ref={announcerRef}
        aria-atomic="true"
        aria-live="polite"
        className="sr-only"
        role="status"
        tabIndex={-1}
      >
        {srAnnouncement}
      </div>
      <ModalHeader
        dispatch={dispatch}
        handleClose={handleClose}
        updatedFiltersData={updatedFiltersData}
      />
      <div className="modal__body">
        <ModalBodyContent
          dispatch={dispatch}
          dragInProgress={dragInProgress}
          editingActive={editingActive}
          setDragInProgress={setDragInProgress}
          setEditingActive={setEditingActive}
          updatedFiltersData={updatedFiltersData}
        />
        <SearchPinsAdd
          dispatch={dispatch}
          updatedFiltersData={updatedFiltersData}
        />
        <ButtonsContainer
          handleClose={handleClose}
          handleSubmit={handleSubmit}
          saveButtonDisabled={saveButtonDisabled}
        />
      </div>
    </div>
  );
};

ManageSearchPinsModal.propTypes = {
  closeModal: PropTypes.func.isRequired,
  searchFilters: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string,
  })).isRequired,
  updateSearchFilters: PropTypes.func.isRequired,
};

export default renderModal(ManageSearchPinsModal, 'manage-search-pins-modal');
